import {TaskLocationData} from "@/shared/timr";
import {LocationPanelData} from "@/shared/locationPanel";

export interface AutoCompleteResult {
	zipCode: string;
	countryCode: string;
	city: string;
	address: string;
	state: string;
	latitude: number;
	longitude: number;
}

export class GoogleMapsAutocomplete {
	public readonly element: google.maps.places.PlaceAutocompleteElement;

	constructor(resultHandler: (result: AutoCompleteResult) => void) {
		this.element = new google.maps.places.PlaceAutocompleteElement({
			// Defined in
			// https://developers.google.com/maps/documentation/places/web-service/supported_types#table3
			includedPrimaryTypes: ["geocode", "establishment"],
		});
		this.element.style.colorScheme = "light";

		this.element.addEventListener("gmp-select", async (event: Event) => {
			const placeSelectEvent = event as Event & {
				placePrediction?: google.maps.places.PlacePrediction;
				place?: google.maps.places.Place;
			};
			const place = placeSelectEvent.placePrediction?.toPlace() ?? placeSelectEvent.place;
			if (!place) {
				return;
			}
			await place.fetchFields({
				fields: ["addressComponents", "location"],
			});
			resultHandler(mapPlaceToAutoCompleteResult(place));
		});
	}

	public clearInput(): void {
		this.element.value = "";
	}

	public setPlaceholder(placeholder: string): void {
		this.element.placeholder = placeholder;
	}
}

// eslint-disable-next-line @typescript-eslint/no-namespace -- grandfathered; new code uses plain module exports
export namespace GeocodeService {
	export async function lookupLatLng(lat: number, lng: number): Promise<AutoCompleteResult> {
		const geocoder = new google.maps.Geocoder();
		const result = await geocoder.geocode({
			location: {lat, lng}
		});

		if (result.results[0] && "address_components" in result.results[0]) {
			return mapGeocoderResultToAutoCompleteResult(result.results[0]);
		} else {
			throw new Error("no result");
		}
	}

	export async function lookupAddress(task: TaskLocationData | LocationPanelData): Promise<google.maps.LatLngLiteral> {
		const geocoder = new google.maps.Geocoder();
		const result = await geocoder.geocode({
			address: task.address + ", " + task.city + ", " + task.countryName,
		});

		if (result.results[0]) {
			const location = result.results[0].geometry.location;
			return location.toJSON();
		} else {
			throw new Error("no result");
		}
	}
}

function mapPlaceToAutoCompleteResult(place: google.maps.places.Place): AutoCompleteResult {
	const components = place.addressComponents ?? [];
	const zipCode = components.find(component => component.types[0] === "postal_code")?.longText;
	const countryCode = components.find(component => component.types[0] === "country")?.shortText;
	const city = components.find(component => component.types[0] === "locality")?.longText;
	const street = components.find(component => component.types[0] === "route")?.longText || "";
	const state = components.find(component => component.types[0] === "administrative_area_level_1")?.longText;
	const streetNumber = components.find(component => component.types[0] === "street_number")?.longText || "";
	const address = street + " " + streetNumber;

	const latitude = place.location?.lat() ?? 0;
	const longitude = place.location?.lng() ?? 0;

	return {
		zipCode, address, city, countryCode, state, latitude, longitude
	};
}

function mapGeocoderResultToAutoCompleteResult(result: google.maps.GeocoderResult): AutoCompleteResult {
	const zipCode = result.address_components.find(component => component.types[0] === "postal_code")?.long_name;
	const countryCode = result.address_components.find(component => component.types[0] === "country")?.short_name;
	const city = result.address_components.find(component => component.types[0] === "locality")?.long_name;
	const street = result.address_components.find(component => component.types[0] === "route")?.long_name || "";
	const state = result.address_components.find(component => component.types[0] === "administrative_area_level_1")?.long_name;
	const streetNumber = result.address_components.find(component => component.types[0] === "street_number")?.long_name || "";
	const address = street + " " + streetNumber;

	const latitude = result.geometry.location.lat();
	const longitude = result.geometry.location.lng();

	return {
		zipCode, address, city, countryCode, state, latitude, longitude
	};
}
